import React from 'react';
import { motion } from 'framer-motion';
import { Zap, FileText, Clock, Award, Star, ShieldCheck, Lock, CreditCard } from 'lucide-react';
export function Hero() {
  return <section className="relative pt-4 pb-12 overflow-hidden" data-id="element-185">
      {/* Background Effects */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-radial-glow pointer-events-none opacity-60" data-id="element-186"></div>
      <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-energy/5 blur-[120px] rounded-full pointer-events-none" data-id="element-187"></div>

      <div className="container mx-auto px-4 relative z-10" data-id="element-188">
        {/* Announcement Bar */}
        <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="flex items-center justify-center mb-6" data-id="element-189">
          <div className="bg-petroleum/80 backdrop-blur-sm border border-electric/30 rounded-full py-2 px-4 md:px-6 inline-flex items-center gap-2 text-sm md:text-base text-textsecondary" data-id="element-190">
            <Zap className="w-4 h-4 text-electric fill-electric" data-id="element-191" />
            <span className="font-semibold text-white" data-id="element-192">
              BIBLIOTECAS DIGITALES™
            </span>
            <span className="hidden md:inline mx-2 text-electric/50" data-id="element-193">•</span>
            <span data-id="element-194">Todo tu hogar eléctrico. Resuelto. Desde hoy.</span>
          </div>
        </motion.div>

        <div className="max-w-5xl mx-auto text-center" data-id="element-195">
          {/* Title */}
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="md:text-5xl lg:text-6xl font-extrabold mb-6 text-[33px]" data-id="element-196">
            Electricidad en Casa:
            <br data-id="element-197" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-electric to-blue-300" data-id="element-198">
              El Sistema "Cero Riesgo"
            </span>
          </motion.h1>

          {/* Mockup Image - directly below title */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.2,
          duration: 0.5
        }} className="relative mb-4" data-id="element-199">
            <div className="absolute inset-0 bg-electric/20 blur-[100px] rounded-full" data-id="element-200"></div>
            <img src="https://cdn.magicpatterns.com/uploads/v1PErkQBQnHbvHhZuoQVNu/muckup_sin_fondo.png" alt="Bundle Electricidad en Casa" className="relative z-10 w-full max-w-[320px] md:max-w-md mx-auto max-h-[46vh] object-contain drop-shadow-2xl hover:scale-105 transition-transform duration-500" data-id="element-201" />
          </motion.div>

          {/* Subhead */}
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="text-lg md:text-xl text-textsecondary mb-5 max-w-2xl mx-auto" data-id="element-202">
            El sistema N°1 para propietarios que quieren autonomía eléctrica
            total.
          </motion.p>

          {/* Trust Badges */}
          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 mb-6 text-sm md:text-base font-medium" data-id="element-203">
            <div className="flex items-center gap-2" data-id="element-204">
              <FileText className="w-5 h-5 text-electric" data-id="element-205" />
              <span className="text-[10px]" data-id="element-206">100% PDF</span>
            </div>
            <div className="hidden md:block w-1 h-1 rounded-full bg-textsecondary/50" data-id="element-207"></div>
            <div className="flex items-center gap-2" data-id="element-208">
              <Clock className="w-5 h-5 text-electric" data-id="element-209" />
              <span className="text-[10px]" data-id="element-210">Acceso en 5 min</span>
            </div>
            <div className="hidden md:block w-1 h-1 rounded-full bg-textsecondary/50" data-id="element-211"></div>
            <div className="flex items-center gap-2" data-id="element-212">
              <Award className="w-5 h-5 text-electric" data-id="element-213" />
              <span className="text-[10px]" data-id="element-214">De 0 a Diagnosticador Pro</span>
            </div>
          </div>

          {/* CTA */}
          <div className="flex flex-col items-center gap-4" data-id="element-215">
            <a href="#pricing" className="w-full md:w-auto bg-gold hover:bg-yellow-400 text-graphite font-bold text-lg md:text-xl py-4 md:py-5 px-8 md:px-12 rounded-xl transition-all duration-300 transform hover:-translate-y-1 glow-gold flex items-center justify-center gap-3" data-id="element-216">
              ⚡ QUIERO EL MANUAL AHORA
            </a>

            <div className="flex flex-wrap items-center justify-center gap-4 text-xs md:text-sm text-textsecondary mt-2" data-id="element-217">
              <div className="flex items-center gap-1" data-id="element-218">
                <ShieldCheck className="w-4 h-4" data-id="element-219" /> Garantía 7 días
              </div>
              <div className="flex items-center gap-1" data-id="element-220">
                <Lock className="w-4 h-4" data-id="element-221" /> Pago seguro
              </div>
              <div className="flex items-center gap-1" data-id="element-222">
                <CreditCard className="w-4 h-4" data-id="element-223" /> Mercado Pago
              </div>
            </div>
          </div>

          {/* Social Proof Mini */}
          <div className="mt-6 flex items-center justify-center gap-4" data-id="element-224">
            <div className="flex -space-x-3" data-id="element-225">
              {[1, 2, 3, 4].map(i => <div key={i} className="w-10 h-10 rounded-full bg-petroleum border-2 border-graphite flex items-center justify-center text-xs font-bold text-electric" data-id="element-226">
                  {String.fromCharCode(64 + i)}
                </div>)}
            </div>
            <div className="flex flex-col items-center" data-id="element-227">
              <div className="flex gap-1 text-gold" data-id="element-228">
                {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-4 h-4 fill-gold" data-id="element-229" />)}
              </div>
              <span className="text-sm font-medium" data-id="element-230">
                <span className="text-white" data-id="element-231">4.9/5</span> • +500 propietarios
                confían
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}